import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dramas } from '@/mocks/dramas';

function Breadcrumb({ items }: { items: { label: string; href?: string }[] }) {
  return (
    <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
      {items.map((item, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 && <i className="ri-arrow-right-s-line text-xs"></i>}
          {item.href ? (
            <Link to={item.href} className="hover:text-primary-600 transition-colors whitespace-nowrap">
              {item.label}
            </Link>
          ) : (
            <span className="text-foreground-800 font-medium whitespace-nowrap">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

function BrandIntro() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <div className="flex-1">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-5">
              关于桔子影视
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              桔子影视成立于2020年，是国内领先的高清电视剧在线观看平台。我们致力于为广大追剧爱好者提供最新、最全、最高清的电视剧资源，让每一位用户都能享受到极致的在线观剧体验。
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              平台收录超过<strong>5000部</strong>电视剧资源，涵盖古装、现代、悬疑、爱情、喜剧、武侠、科幻、都市等多种类型，每日实时更新，确保您不错过任何一部热播好剧。
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              我们的使命是让每一个人都能轻松找到并观看自己喜欢的电视剧。无论您喜欢什么类型的剧集，桔子影视都能满足您的追剧需求。
            </p>
          </div>
          <div className="flex-shrink-0 w-full sm:w-80 lg:w-96">
            <div className="aspect-[4/3] rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20home%20theater%20setup%20with%20large%20screen%20displaying%20cinematic%20content%2C%20cozy%20living%20room%20with%20warm%20ambient%20lighting%2C%20comfortable%20seating%2C%20clean%20contemporary%20design%2C%20evening%20mood%2C%20professional%20interior%20photography&width=800&height=600&seq=about-brand&orientation=landscape"
                alt="桔子影视 - 品牌形象"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const stats = [
    { num: '5000+', label: '电视剧资源', icon: 'ri-movie-line' },
    { num: '1000万+', label: '月活用户', icon: 'ri-user-line' },
    { num: '99.9%', label: '服务可用率', icon: 'ri-shield-check-line' },
    { num: '4.9', label: '用户评分', icon: 'ri-star-line' },
  ];

  return (
    <section className="py-12 md:py-16 bg-primary-500">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <span className="w-12 h-12 flex items-center justify-center bg-background-50/20 rounded-xl mx-auto mb-3">
                <i className={`${stat.icon} text-xl text-background-50`}></i>
              </span>
              <div className="text-2xl md:text-3xl font-black text-background-50">{stat.num}</div>
              <div className="text-sm text-background-50/80 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const reviews = [
    {
      name: '追剧达人小王',
      text: '桔子影视真的太好用了！资源超级全，更新速度也很快，画质清晰流畅。自从发现了这个平台，追剧再也不愁了，强烈推荐！',
      rating: 5,
    },
    {
      name: '影视爱好者李姐',
      text: '界面简洁干净，分类清晰，找剧特别方便。最重要的是完全免费，而且缓冲速度快。古装剧资源特别丰富，是我的追剧首选平台。',
      rating: 5,
    },
    {
      name: '大学生小张',
      text: '室友推荐给我的，用了半年了，体验一直很好。支持多种设备观看，在宿舍用电脑看，上课路上用手机看，非常方便。',
      rating: 5,
    },
    {
      name: '程序员老刘',
      text: '作为一个技术宅，我特别在意画质和加载速度。桔子影视在这两个方面都做得很好，高清画质非常清晰，播放也很流畅。',
      rating: 4,
    },
    {
      name: '全职妈妈小陈',
      text: '带娃间隙就喜欢追追剧放松一下，桔子影视的电视剧资源真的很全，而且免费。每天都会来看看有没有更新，已经推荐给朋友们了。',
      rating: 5,
    },
    {
      name: '退休教师老周',
      text: '孩子们教我用桔子影视看剧，很容易上手。分类很清楚，想看什么类型的都能找到。老剧资源也很丰富，重温了不少经典。',
      rating: 5,
    },
  ];

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
            用户口碑
          </h2>
          <p className="text-sm text-foreground-500 mt-2">来自真实用户的评价</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 stagger-children">
          {reviews.map((review, i) => (
            <div key={i} className="bg-background-50 rounded-xl p-5 md:p-6">
              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, j) => (
                  <i
                    key={j}
                    className={`text-sm ${
                      j < review.rating ? 'ri-star-fill text-secondary-500' : 'ri-star-line text-foreground-300'
                    }`}
                  ></i>
                ))}
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                "{review.text}"
              </p>
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-user-line text-primary-600"></i>
                </span>
                <span className="text-sm font-semibold text-foreground-800">{review.name}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    const formData = new FormData(form);
    formData.delete('phone_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9nh1mkit35ii3jdlr60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormStatus('error');
          setFormError(msg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
          <div className="flex-1">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-8">
              如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们，我们将在24小时内回复您。
            </p>

            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-primary-600"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">电子邮箱</p>
                  <p className="text-sm font-medium text-foreground-800">contact@juzitv.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-primary-600"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">客服电话</p>
                  <p className="text-sm font-medium text-foreground-800">400-888-6666</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-primary-600"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">公司地址</p>
                  <p className="text-sm font-medium text-foreground-800">北京市朝阳区影视产业园A座15层</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1">
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-100 rounded-xl p-6 md:p-8"
            >
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-5">发送消息</h3>

              {formStatus === 'success' && (
                <div className="mb-4 p-4 bg-accent-100/60 text-accent-800 rounded-lg text-sm">
                  <i className="ri-checkbox-circle-line mr-2"></i>
                  消息已发送成功！我们会在24小时内回复您。
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  {formError}
                </div>
              )}

              <div className="flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="请输入消息主题"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                    placeholder="请输入您的消息（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-phone-alt"
                />

                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-lg transition-all hover:scale-[1.02] cursor-pointer text-sm whitespace-nowrap"
                >
                  <i className="ri-send-plane-line mr-2"></i>
                  发送消息
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <main className="pt-20 min-h-screen">
      <BrandIntro />
      <Stats />
      <Testimonials />
      <ContactSection />

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于桔子影视',
            description: '桔子影视是国内领先的高清电视剧在线观看平台，收录5000+部电视剧资源。',
            mainEntity: {
              '@type': 'Organization',
              name: '桔子影视',
              description: '国内领先的高清电视剧在线观看平台',
              url: 'https://juzitv.com',
              contactPoint: {
                '@type': 'ContactPoint',
                telephone: '400-888-6666',
                contactType: 'customer service',
                availableLanguage: 'Chinese',
              },
              address: {
                '@type': 'PostalAddress',
                streetAddress: '朝阳区影视产业园A座15层',
                addressLocality: '北京',
                addressCountry: 'CN',
              },
            },
          }),
        }}
      />
    </main>
  );
}